/**
 * LoginPage Component
 *
 * Modern professional login page with split-screen design
 * Features:
 * - Split layout with branding panel and form panel
 * - Clean, modern input styling
 * - Smooth animations and transitions
 * - Loading states and error handling
 */

import { useState, useEffect, useRef, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  EyeOutlined,
  EyeInvisibleOutlined,
  LoadingOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { message } from 'antd';
import authService from '@modules/auth/services/authService';
import type { TenantPickerOption } from '@modules/auth/services/authService';
import type { LoginResponse } from '@/types/models';
import { useAuth } from '@/hooks';
import { useBrand, useBrandAssets, getActiveCompanyTenantCode } from '@/brand';
import { LoginBrandingPanel } from '@modules/auth/components/LoginBrandingPanel';
import { useLanguage } from '@/hooks/useLanguage';
import { buildUserHomePath } from '@/routing';
import brijIcon from '@/brand-assets/brij/icon-black.svg';
import './LoginPage.css';

import { PUBLIC_DOCUMENTS_PUBLISHED } from '@modules/legal/publicPaths';
/**
 * Multi-tenant chooser shown after the email/password submit when a
 * company has `multi_user_login_allowed=true` and the credentials
 * match more than one sub-tenant user. Click a tenant → completes
 * the login as that specific account. Plain inline styles only so
 * the picker inherits the tenant's brand surface without needing a
 * dedicated CSS file.
 */
function TenantPicker({
  tenants,
  onPick,
  onCancel,
  loading,
  error,
}: {
  tenants: TenantPickerOption[];
  onPick: (tenantId: string) => void;
  onCancel: () => void;
  loading: boolean;
  error: string;
}) {
  const { t } = useLanguage();
  return (
    <div className="login-form" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p style={{ opacity: 0.75, marginTop: 0 }}>
        {t('login.yourAccountExistsInMore')}
      </p>
      {error && (
        <div className="error-message">
          <ExclamationCircleOutlined className="error-icon" />
          <span dir="auto" className="error-text">{error}</span>
        </div>
      )}
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {tenants.map((t) => (
          <li key={t.tenant_id}>
            <button
              type="button"
              onClick={() => onPick(t.tenant_id)}
              disabled={loading}
              style={{
                width: '100%',
                textAlign: 'start',
                padding: '12px 16px',
                borderRadius: 10,
                border: '1px solid var(--border, rgba(0,0,0,0.12))',
                background: 'var(--surface, transparent)',
                cursor: loading ? 'wait' : 'pointer',
                color: 'inherit',
              }}
            >
              <div style={{ fontWeight: 600 }} dir="auto">{t.tenant_name || t.tenant_code || 'Workspace'}</div>
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }} dir="auto">
                {t.tenant_code} · {t.user_role}
              </div>
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={onCancel}
        disabled={loading}
        style={{
          background: 'transparent',
          border: 'none',
          color: 'inherit',
          opacity: 0.7,
          textDecoration: 'underline',
          cursor: 'pointer',
          padding: 4,
          marginTop: 4,
        }}
      >
        {t('login.backToSignIn')}
      </button>
    </div>
  );
}

export interface LoginPageProps {
  /**
   * Whether the "forgot password?" affordance is available for this login
   * surface. Threaded from the tenant's resolved `auth.transactional_email`
   * flag (see PublicTenantLogin). Defaults to TRUE so the link is never
   * hidden spuriously — only hidden when the caller positively knows the
   * tenant has transactional email disabled.
   */
  forgotPasswordEnabled?: boolean;
}

export default function LoginPage({ forgotPasswordEnabled = true }: LoginPageProps) {
  const navigate = useNavigate();
  const brand = useBrand();
  // anniversaryLogo: temporary 10th-anniversary lockup, falls back to the
  // normal logo everywhere it isn't set (see brand/types.ts).
  const { anniversaryLogo: loginLogo } = useBrandAssets();
  const { isAuthenticated, user } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  // "Remember me": the email this browser remembers starts the form, with
  // the box still ticked. The password is the browser's password manager's
  // business (autocomplete below) — the platform never stores it.
  const [rememberedEmail, setRememberedEmail] = useState(() => authService.getRememberedEmail());
  const [email, setEmail] = useState(rememberedEmail);
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(() => !!rememberedEmail);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // When the URL is a company with multi_user_login_allowed AND the
  // email matches more than one sub-tenant user, the backend returns
  // a picker payload instead of a token. We hold those options here
  // and render a chooser; selection re-calls login with the chosen
  // tenant_id pinned.
  const [tenantOptions, setTenantOptions] = useState<TenantPickerOption[] | null>(null);
  // Forgot-password flow: in-card view swap (same pattern as the tenant
  // picker). 'forgot' shows the email form; 'forgot_sent' the generic
  // confirmation — always shown regardless of whether the email matched
  // an account (existence is never revealed).
  const [view, setView] = useState<'login' | 'forgot' | 'forgot_sent'>('login');
  const [forgotEmail, setForgotEmail] = useState('');

  // Redirect already-authenticated users to their home — but ONLY
  // when the URL's tenant slug matches their tenant. If they pasted
  // another tenant's login URL while signed in to their own, render
  // the login form so they can authenticate as a different user
  // (per-tab sessionStorage keeps any other tab's session intact).
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const urlSlug = (window.location.pathname.match(/^\/([^/]+)\/login\/?$/i)?.[1] || '').toLowerCase();
    const userSlug = (user.company_tenant_code || user.tenant_code || '').toLowerCase();
    // No tenant slug in URL (legacy /login redirect) OR slug matches
    // user's tenant — safe to redirect home.
    if (!urlSlug || urlSlug === userSlug) {
      navigate(buildUserHomePath(user), { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  // Brand hard-refresh on every login-page visit.
  // The cached brand_overrides in localStorage persist across logout, so a user
  // who previously logged into M² and now visits brij.corpbrain.com would
  // briefly see the M² login UI until the host resolver finishes. Clearing the
  // cache + the entry-splash session key on mount forces the resolver chain
  // (URL `?brand=` → host → default) to re-run fresh and guarantees the splash
  // plays again after the next successful login.
  useEffect(() => {
    try {
      localStorage.removeItem('brand_overrides_cache');
      sessionStorage.removeItem('corpbrain_splash_shown');
    } catch {
      // localStorage / sessionStorage can throw under some sandbox modes —
      // failing here would block the login page from rendering, which is
      // strictly worse than a stale brand cache.
    }
  }, []);

  /**
   * Shared post-success handler: enforces the URL-slug-matches-user
   * rule and navigates the user home. Used by both the initial
   * submit and the multi-tenant picker callback.
   */
  const completeLogin = (response: LoginResponse): boolean => {
    const urlSlug = (window.location.pathname.match(/^\/([^/]+)\/login\/?$/i)?.[1] || '').toLowerCase();
    const userSlug = (response.user.company_tenant_code || response.user.tenant_code || '').toLowerCase();
    if (urlSlug && userSlug && urlSlug !== userSlug) {
      // Ends what this login just opened, the remembered sign-in included.
      void authService.logout();
      setError(t('login.incorrectCredentials'));
      return false;
    }
    message.success(t('login.loginSuccessful'));
    navigate(buildUserHomePath(response.user), { replace: true });
    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setTenantOptions(null);
    setLoading(true);

    try {
      const companyTenantCode = getActiveCompanyTenantCode() ?? undefined;
      const response = await authService.login(email, password, 'test', companyTenantCode, undefined, remember);

      if ('pick_tenant' in response) {
        // Multi-user company + ambiguous email — show picker. We
        // intentionally keep `email`/`password` in component state so
        // the picker click can re-call login with the same creds.
        setTenantOptions(response.tenants);
        setLoading(false);
        return;
      }

      completeLogin(response);
    } catch (err: any) {
      setError(err.message || t('login.failedCheckCredentials'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Picker callback: re-runs the login with `targetUserTenantId`
   * pinned to the chosen sub-tenant. Email + password are still in
   * component state from the first submit.
   */
  const handlePickTenant = async (tenantId: string) => {
    setError('');
    setLoading(true);
    try {
      const companyTenantCode = getActiveCompanyTenantCode() ?? undefined;
      const response = await authService.login(email, password, 'test', companyTenantCode, tenantId, remember);
      if ('pick_tenant' in response) {
        // Should not happen on the pinned call, but guard anyway.
        setError(t('login.couldNotComplete'));
        return;
      }
      completeLogin(response);
    } catch (err: any) {
      setError(err.message || t('login.failedCheckCredentials'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelPicker = () => {
    setTenantOptions(null);
    setError('');
  };

  /** "Not you?" — forget the remembered person and start an empty form. */
  const handleNotYou = () => {
    authService.forgetRememberedUser();
    setRememberedEmail('');
    setEmail('');
    setPassword('');
    setRemember(false);
    emailInputRef.current?.focus();
  };

  const openForgotView = () => {
    setError('');
    setForgotEmail(email); // prefill from whatever they typed in the login form
    setView('forgot');
  };

  const backToLogin = () => {
    setError('');
    setView('login');
  };

  const handleForgotSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const companyTenantCode = getActiveCompanyTenantCode() ?? undefined;
      await authService.requestPasswordReset(forgotEmail, companyTenantCode);
      setView('forgot_sent');
    } catch (err: any) {
      setError(err.message || t('auth.forgot.error', { defaultValue: 'Could not submit the request. Please try again.' }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      {/* Language switch — the login page is the one screen a person reaches
          before we know anything about them, so it has to carry its own way
          into Hebrew. Without it a Hebrew speaker had no route to an RTL app
          at all: the preference only existed in Settings, behind the login. */}
      <div className="login-language-switch" role="group" aria-label={t('login.languageToggleAria')}>
        <button
          type="button"
          className={`login-language-option ${language === 'en' ? 'active' : ''}`}
          onClick={() => setLanguage('en')}
          aria-pressed={language === 'en'}
          lang="en"
        >
          EN
        </button>
        <button
          type="button"
          className={`login-language-option ${language === 'he' ? 'active' : ''}`}
          onClick={() => setLanguage('he')}
          aria-pressed={language === 'he'}
          lang="he"
        >
          עב
        </button>
      </div>

      {/* Branding panel — visually on the right (flex-direction: row-reverse).
          Shared with the White-Label editor's live preview, so the preview
          renders the real thing rather than a lookalike that can drift. */}
      <LoginBrandingPanel brand={brand} logo={loginLogo} />

      {/* Login form panel — visually on the left (flex-direction: row-reverse) */}
      <div className="login-form-panel">
        <div className="form-container">
          <div className="form-header">
            <h1 className="form-title">
              {tenantOptions
                ? t('login.chooseWorkspace')
                : view === 'forgot'
                  ? t('auth.forgot.title', { defaultValue: 'Forgot your password?' })
                  : view === 'forgot_sent'
                    ? t('auth.forgot.sentTitle', { defaultValue: 'Check your inbox' })
                    : t('login.welcomeBackExclaim')}
            </h1>
          </div>

          {tenantOptions ? (
            <TenantPicker
              tenants={tenantOptions}
              onPick={handlePickTenant}
              onCancel={handleCancelPicker}
              loading={loading}
              error={error}
            />
          ) : view === 'forgot' ? (
            <form className="login-form" onSubmit={handleForgotSubmit}>
              {error && (
                <div className="error-message">
                  <ExclamationCircleOutlined className="error-icon" />
                  <span dir="auto" className="error-text">{error}</span>
                </div>
              )}
              <p style={{ marginBottom: 16, color: 'var(--text-secondary, #6b7280)' }}>
                {t('auth.forgot.subtitle', { defaultValue: "Enter your email address and we'll send you a link to reset your password." })}
              </p>
              <div className="form-group">
                <label htmlFor="forgot-email" className="form-label">
                  {t('auth.forgot.emailLabel', { defaultValue: 'Email Address' })}
                </label>
                <div className="input-row">
                  <div className="input-wrapper">
                    <input dir="auto"
                      type="email"
                      id="forgot-email"
                      className="form-input"
                      placeholder={t('login.emailAddressPlaceholder')}
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                      autoComplete="email"
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
              <button type="submit" className="login-btn" disabled={loading || !forgotEmail}>
                {loading ? (
                  <>
                    <LoadingOutlined className="btn-spinner" />
                    <span>{t('auth.forgot.submitting', { defaultValue: 'Sending…' })}</span>
                  </>
                ) : (
                  <span>{t('auth.forgot.submit', { defaultValue: 'Send reset link' })}</span>
                )}
              </button>
              <div className="form-options" style={{ justifyContent: 'center', marginTop: 16 }}>
                <a
                  href="#"
                  className="forgot-link"
                  onClick={(e) => { e.preventDefault(); backToLogin(); }}
                >
                  {t('auth.forgot.back', { defaultValue: 'Back to sign in' })}
                </a>
              </div>
            </form>
          ) : view === 'forgot_sent' ? (
            <div className="login-form">
              <p style={{ marginBottom: 20, color: 'var(--text-secondary, #6b7280)' }}>
                {t('auth.forgot.sentBody', { defaultValue: 'If an account exists for that address, a password reset link is on its way. The link expires in 1 hour.' })}
              </p>
              <button type="button" className="login-btn" onClick={backToLogin}>
                {t('auth.forgot.back', { defaultValue: 'Back to sign in' })}
              </button>
            </div>
          ) : (
          <form className="login-form" onSubmit={handleSubmit}>
            {/* Error Message */}
            {error && (
              <div className="error-message">
                <ExclamationCircleOutlined className="error-icon" />
                <span dir="auto" className="error-text">{error}</span>
              </div>
            )}

            {/* Email Field */}
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                {t('login.emailAddress')}
              </label>
              <div className="input-row">
                <div className="input-wrapper">
                  <input dir="auto"
                    ref={emailInputRef}
                    type="email"
                    id="email"
                    name="email"
                    className="form-input"
                    placeholder="name@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="username"
                  />
                </div>
              </div>
              {rememberedEmail && email === rememberedEmail && (
                <button type="button" className="login-not-you" onClick={handleNotYou}>
                  {t('login.notYou')}
                </button>
              )}
            </div>

            {/* Password Field */}
            <div className="form-group">
              <label htmlFor="password" className="form-label">
                {t('login.password')}
              </label>
              <div className="input-row">
                <div className="input-wrapper">
                  <input dir="auto"
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    className="form-input has-toggle"
                    placeholder={t('login.passwordPlaceholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    autoFocus={!!rememberedEmail}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={t('login.togglePasswordVisibility')}
                  >
                    {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                  </button>
                </div>
              </div>
            </div>

            {/* Remember Me + Forgot Password */}
            <div className="form-options">
              <label className="checkbox-container">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                />
                <span className="checkmark"></span>
                <span className="checkbox-text">{t('login.rememberMe')}</span>
              </label>
              {forgotPasswordEnabled && (
                <a
                  href="#"
                  className="forgot-link"
                  onClick={(e) => {
                    e.preventDefault();
                    openForgotView();
                  }}
                >
                  {t('login.forgotPassword', { defaultValue: 'Forgot password?' })}
                </a>
              )}
            </div>

            {/* Login Button */}
            <button type="submit" className="login-btn" disabled={loading}>
              {loading ? (
                <>
                  <LoadingOutlined className="btn-spinner" />
                  <span>{t('login.signingIn')}</span>
                </>
              ) : (
                <span>{t('login.signIn')}</span>
              )}
            </button>
          </form>
          )}

          <div className="form-footer">
            <p className="footer-text">
              {t('login.encryptionNote')}
            </p>
            {PUBLIC_DOCUMENTS_PUBLISHED && (
              <div className="legal-links">
                <a href="/privacy">{t('legal.nav.privacy')}</a>
                <span className="legal-separator" aria-hidden="true">·</span>
                <a href="/terms">{t('legal.nav.terms')}</a>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Brij attribution — fixed at bottom-right of the viewport */}
      <a
        href="https://brijlabs.ai/"
        target="_blank"
        rel="noopener noreferrer"
        className="powered-by-brij"
        aria-label={t('login.poweredByBrij')}
      >
        <span className="powered-by-text">
          {t('common.poweredBy')} <span className="powered-by-brand">Brij</span>
        </span>
        <img src={brijIcon} alt="" className="powered-by-icon" aria-hidden="true" />
      </a>
    </div>
  );
}
